import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
// import RumSDK from '@arms/rum-browser'
import { useAdChannelStore } from '@/store/adChannelCode.store'
import { getDomainOrigin, getFullUrl, isInWeChat } from '@/utils/common-js'

type RumSDKModule = typeof import('@arms/rum-browser')
export const initRumSDKInstance = (() => {
  let rumSDKTaskQueue: Array<(sdk: RumSDKModule['default']) => void> = []
  let rumSDKInstance: RumSDKModule['default'] | null = null
  return async (task: (sdk: RumSDKModule['default']) => void) => {
    if (rumSDKInstance) {
      task(rumSDKInstance)
      return
    }
    rumSDKTaskQueue.push(task)
    import('@arms/rum-browser').then(rumSDK => {
      rumSDKInstance = rumSDK.default
      rumSDKTaskQueue.forEach(task => task(rumSDKInstance as RumSDKModule['default']))
      rumSDKTaskQueue = []
    })
  }
})()


/**
 * 上报自定义事件到 ARMS
 */
export const reportCustomEvent = (
  type: string,
  name: string,
  value: number,
  properties?: Record<string, string | number | boolean | null>
) => {
  try {
    initRumSDKInstance(rumSDK => rumSDK.sendCustom({
      type,
      name,
      value: typeof value === 'number' ? value : 1,
      properties
    }))
  } catch (err) {
    console.error('[ARMS] sendCustom error:', err)
  }
}

/**
 * 跟踪跳转延迟
 * - 3s 内跳走：不上报
 * - 3s 后跳走：上报 route_jump_delay，携带 waitDuration（总等待时长）
 * @param extra 额外的上报参数
 */
export const trackDelayedJump = (
  extra?: Record<string, string | number | boolean | null>
) => {
  const startTime = Date.now()
  let maxWaitTime = 10000
  
  
  const timerId = setTimeout(() => {
    if (document.hidden) return
    // 使用捕获阶段，确保在 ARMS flush 之前执行
    document.addEventListener('visibilitychange', handleVisibilityChange, true)
  }, 3000)

  const cleanup = () => {
    clearTimeout(timerId)
    document.removeEventListener('visibilitychange', handleVisibilityChange, true)
  }

  const handleVisibilityChange = () => {
    if (!document.hidden) return
    // 3s 后跳走，上报等待时长
    const adChannelCode = useAdChannelStore.getState().adChannelCode
    let waitDuration = Date.now() - startTime || 0
    reportCustomEvent('route_jump_delay', `${adChannelCode}-${extra?.productId || ''}`, Number((waitDuration / 1000).toFixed(2)) ,{
      adChannelCode,
      currentUrl: getFullUrl(),
      waitDuration,
      ...extra,
    })
    cleanup()
  }

  // 10s 后强制清理，避免内存泄漏
  setTimeout(cleanup, maxWaitTime)
}

/**
 * 500ms 内连续路由跳转检测
 * 检测短时间内频繁跳转的异常情况
 */
export const useRouteRapidJumpTracker = (threshold: number = 500) => {
  const location = useLocation()
  const lastJumpTimeRef = useRef<number>(0)
  const lastPathRef = useRef<string>('')

  useEffect(() => {
    const now = Date.now()
    const timeDiff = now - lastJumpTimeRef.current
    const previousPath = lastPathRef.current

    // 首次加载不计入
    if (lastJumpTimeRef.current > 0 && timeDiff < threshold) {
      reportCustomEvent('route_rapid_jump', `${previousPath}-${location.pathname}`, Number((timeDiff / 1000).toFixed(2)),{
        from_path: previousPath,
        to_path: location.pathname,
        time_diff: timeDiff
      })
    }

    lastJumpTimeRef.current = now
    lastPathRef.current = location.pathname
  }, [location.pathname, threshold])
}

export default useRouteRapidJumpTracker

// 微信环境上报域名
export const reportWechatDomain = () => {
  useEffect(() => { 
    if (!isInWeChat) {
      return
    }
    setTimeout(() => {
      reportCustomEvent('微信环境域名', getDomainOrigin(), 1, {
        domain: getDomainOrigin(),
        currentUrl: getFullUrl(),
        adChannelCode: useAdChannelStore.getState().adChannelCode
      })
    }, 0)
  }, [])
} 